import type { OutboxObservationItem } from './types';
import { createPrefixedLogger } from '../../utils/logger';

const log = createPrefixedLogger('outboxStorage');

/**
 * Web outbox persistence: raw IndexedDB (no new dependency). Metro resolves
 * this file on web; outboxStorage.ts is the native counterpart. DB
 * `redi-outbox` holds two object stores: `items` (JSON-serializable
 * OutboxObservationItem records, keyed by item.id) and `blobs` (compressed
 * photo Blobs, keyed by that same id or `${id}-before`).
 *
 * Unlike shardCache.ts's backend (which swallows failures and returns
 * null/[] — a cache miss is harmless there), an open/transaction failure
 * here rejects: the outbox is durable storage, and the sync engine needs to
 * know when it can't persist so it doesn't report an item as queued when it
 * isn't.
 */

const DB_NAME = 'redi-outbox';
const DB_VERSION = 1;
const ITEMS_STORE = 'items';
const BLOBS_STORE = 'blobs';

let _db: IDBDatabase | null = null;
let _dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ITEMS_STORE)) db.createObjectStore(ITEMS_STORE);
      if (!db.objectStoreNames.contains(BLOBS_STORE)) db.createObjectStore(BLOBS_STORE);
    };
    req.onsuccess = () => {
      _db = req.result;
      resolve(req.result);
    };
    req.onerror = () => {
      log.warn('IndexedDB open failed; outbox storage unavailable');
      _dbPromise = null;
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
  });
  return _dbPromise;
}

// Lightweight shape check, mirroring shardCache's isValidShardCacheEntry —
// a corrupt/tampered record is treated as absent rather than crashing the
// engine's hydrate pass.
function isValidItem(v: unknown): v is OutboxObservationItem {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    o.kind === 'observation' &&
    typeof o.authorId === 'string' &&
    typeof o.createdAtMs === 'number' &&
    typeof o.status === 'string'
  );
}

export async function listItems(): Promise<OutboxObservationItem[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ITEMS_STORE, 'readonly');
    const req = tx.objectStore(ITEMS_STORE).getAll();
    req.onsuccess = () => resolve(((req.result ?? []) as unknown[]).filter(isValidItem));
    req.onerror = () => reject(req.error ?? new Error('listItems failed'));
  });
}

export async function putItem(item: OutboxObservationItem): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ITEMS_STORE, 'readwrite');
    tx.objectStore(ITEMS_STORE).put(item, item.id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('putItem failed'));
  });
}

export async function removeItem(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ITEMS_STORE, 'readwrite');
    tx.objectStore(ITEMS_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('removeItem failed'));
  });
}

export async function putBlob(key: string, blob: Blob): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BLOBS_STORE, 'readwrite');
    tx.objectStore(BLOBS_STORE).put(blob, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('putBlob failed'));
  });
}

export async function getBlob(key: string): Promise<Blob | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BLOBS_STORE, 'readonly');
    const req = tx.objectStore(BLOBS_STORE).get(key);
    req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error('getBlob failed'));
  });
}

export async function removeBlob(key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BLOBS_STORE, 'readwrite');
    tx.objectStore(BLOBS_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('removeBlob failed'));
  });
}
